import { Dispatch, SetStateAction } from "react";
import { tx } from "modules/language/translate";
import { regexValidators } from "modules/utils/form";

import "./AuthForms.scss";

import {
  Link,
  Input,
  Layout,
  FormRow,
  InputLabel,
  validateInput,
} from "frontend-components";

export type LoginType = "oauth" | "dashboard";

export type EmailPasswordFormValues = {
  email: string;
  pending_email?: string;
  password: string;
  otp?: string;
};

export const formValidators = {
  email: (emailValue = "") =>
    validateInput({
      value: emailValue,
      label: tx("auth.attributes.email"),
      validators: {
        required: true,
        regexMatch: regexValidators.email,
      },
    }),
  password: (passwordValue = "") =>
    validateInput({
      value: passwordValue,
      label: tx("auth.attributes.password"),
      validators: {
        required: true,
        minCharacters: 8,
      },
    }),
};

export const formFieldsInvalid = (form: EmailPasswordFormValues) => {
  return Object.entries(formValidators)
    .map(
      ([fieldName, validator]) =>
        // run the validator functions for each form field
        validator(form[fieldName as keyof EmailPasswordFormValues]).valid
    )
    .some((r) => !r);
};

/**
 * Only the email and password portions of the login form
 * that include form field validation
 */
export const EmailPasswordFormFragment = ({
  email,
  form,
  setForm,
}: {
  email?: string;
  form: EmailPasswordFormValues;
  setForm: Dispatch<SetStateAction<EmailPasswordFormValues>>;
}) => {
  return (
    <>
      <FormRow margin="sm">
        <Input
          id="login-email"
          data-testid="login-email-input"
          label={<InputLabel text={tx("auth.attributes.email")} />}
          fullscreen={true}
          onChange={(e) => {
            const email = e.target.value;
            setForm((previousValues) => {
              return { ...previousValues, email };
            });
          }}
          validate={() => formValidators.email(form.email)}
          disabled={!!email}
          value={email ? email : form.email}
        />
      </FormRow>
      <FormRow margin="sm">
        <Input
          id="login-password"
          data-testid="login-password-input"
          type="password"
          label={
            <Layout
              flow="left-right"
              width="full"
              justifyContent="space-between"
            >
              <InputLabel text={tx("auth.attributes.password")} />
              <Link href="/forgot-password">
                {tx("auth.actions.forgot_password")}?
              </Link>
            </Layout>
          }
          fullscreen={true}
          onChange={(e) => {
            const password = e.target.value;
            // TODO: this onChange contains the latest value
            //  but `validate` parameter will be using the state value
            //  which will be off by one.
            setForm((previousValues) => {
              return { ...previousValues, password };
            });
          }}
          value={form.password}
          validate={() => formValidators.password(form.password)}
        />
      </FormRow>
    </>
  );
};
